import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/index';
import { Button } from '../components/Button';

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [canResend, setCanResend] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)

  const forgotPasswordMutation = useMutation({
    mutationFn: () => authAPI.forgotPassword(email),
    onSuccess: () => {
      toast.success('Check your email for reset instructions')
      setIsSubmitted(true)
      setCanResend(false)
      setTimeRemaining(600) // 10 minutes in seconds
    },
    onError: (error: any) => {
      console.error('Forgot password error:', error)
      // Don't reveal whether email exists for security
      toast.success('Check your email for reset instructions')
      setIsSubmitted(true)
      setCanResend(false)
      setTimeRemaining(600)
    },
  })

  // Timer for resend button
  useEffect(() => {
    if (timeRemaining <= 0) {
      setCanResend(true)
      return
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [timeRemaining])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      toast.error('Please enter your email address')
      return
    }
    forgotPasswordMutation.mutate()
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy text-white flex-col justify-center items-center p-12">
        <div className="max-w-md">
          <div className="mb-8 flex justify-center">
            <img src="/image.png" alt="RTO Logo" className="h-28 rounded-lg object-cover" />
          </div>
          <div className="text-5xl font-bold mb-6 text-center">RTO Agents</div>
          <p className="text-xl text-gray-300 mb-8">
            Manage your agency's vehicle documents with ease
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Complete document management system</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Real-time expiry tracking and alerts</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Team collaboration and permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Forgot Password form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-white">
        <div className="max-w-md w-full">
          {!isSubmitted ? (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Back to login"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">Forgot Password?</h1>
                <p className="text-gray-600 mt-2">
                  No worries! Enter your email and we'll send you instructions to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={forgotPasswordMutation.isPending}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={forgotPasswordMutation.isPending}
                  disabled={forgotPasswordMutation.isPending}
                >
                  Send Reset Link
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-center text-gray-600 text-sm">
                  Remember your password?{' '}
                  <Link to="/login" className="text-saffron hover:text-orange-600 font-medium">
                    Sign in instead
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
                <p className="text-gray-600 mb-6">
                  If an account exists with this email, you'll receive a password reset link shortly.
                </p>
                <p className="text-sm text-gray-500 mb-8">
                  The reset link will expire in 1 hour.
                </p>

                <Link to="/login">
                  <Button variant="secondary" className="w-full">
                    Back to Sign In
                  </Button>
                </Link>

                <p className="text-center text-gray-600 text-sm mt-4">
                  Didn't receive the email?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      if (canResend) {
                        setIsSubmitted(false)
                        setEmail('')
                        setCanResend(false)
                        setTimeRemaining(0)
                      }
                    }}
                    disabled={!canResend}
                    className={`font-medium ${
                      canResend
                        ? 'text-saffron hover:text-orange-600 cursor-pointer'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {timeRemaining > 0
                      ? `Try again (${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')})`
                      : 'Try again'}
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
